/*
 * Copyright (c) 2025 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
'use client';!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:mta-learning":true});}catch(e){}}();

import { MtaStoryblokTypes } from '@mta/vela-api-types'
import { ArrowRight, Timer, Users } from 'lucide-react'
import Image from 'next/image'
import { useTranslations } from 'next-intl'
import React from 'react'
import { ISbStoryData } from 'storyblok-js-client'

import { ManualRichTextRenderer } from '@/components/dashboard/manuals/ui/ManualRichtextRenderer'
import { ComplexityPill } from '@/components/storyblok/components/manuals/ComplexityPill'
import { Link } from '@/i18n/navigation'
import { cn } from '@/utils/css'

/** A description of the available properties for this component. */
type ActivityPreviewCardProps = {
  /** The raw Storyblok data for the main kit details. */
  kit: ISbStoryData<MtaStoryblokTypes.KitDetailsStoryblok>

  /** The raw Storyblok data for the activity. */
  activity: ISbStoryData<MtaStoryblokTypes.ActivityStoryblok>

  /** The raw Storyblok data for the module, if the activity belongs to a module. */
  module?: ISbStoryData<MtaStoryblokTypes.ModuleDetailsStoryblok>

  /** Whether the activity summary should be visible in the card. */
  showActivitySummary?: boolean

  /** Classes to use on the main wrapper element. */
  className?: string
}

/**
 * Card for a quick preview of an activity. Displays quick, easily digested information (i.e. learning outcomes,
 * participant counts, time duration etc.) for easy previewing of an activity within a manual.
 */
const ActivityPreviewCard: React.FC<ActivityPreviewCardProps> = ({
  kit,
  activity,
  module,
  showActivitySummary = true,
  className,
}) => {
  const t = useTranslations('ModuleActivityPreviewCard')
  const activityPath = module
    ? `/dashboard/manuals/${kit.slug}/${module.slug}/${activity.slug}`
    : `/dashboard/manuals/${kit.slug}/${activity.slug}`

  return (
    <div
      className={cn(
        'bg-manuals-card-bg rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200 mb-4 overflow-hidden border border-gray-200',
        className,
      )}
    >
      <div className='p-4 flex items-stretch justify-between flex-wrap sm:flex-nowrap gap-4'>
        {activity.content.thumbnail?.filename && (
          <div className='relative w-40 flex-shrink-0'>
            <Image
              src={activity.content.thumbnail.filename}
              alt={`${activity.name} thumbnail`}
              fill
              className='rounded-lg object-cover'
            />
          </div>
        )}
        {!activity.content.thumbnail?.filename && kit.content.logo && (
          <div className='relative w-40 flex-shrink-0 bg-white border rounded-lg'>
            <Image
              src={kit.content.logo.filename}
              alt={`${activity.name} thumbnail`}
              fill
              className='object-contain p-4'
              data-src={kit.content.logo.filename}
            />
          </div>
        )}
        <div className='flex-grow'>
          <h3 className='text-lg font-semibold text-gray-900 leading-tight mb-1 flex items-center flex-wrap'>
            {activity.name}
            {activity.content.complexity && (
              <div className='ml-3 mt-1 sm:mt-0'>
                <ComplexityPill complexity={activity.content.complexity} />
              </div>
            )}
          </h3>
          <div className='flex items-center space-x-3 text-sm text-gray-700 mb-2'>
            <div className='flex items-center'>
              <Timer className='mr-1 h-4 w-4' />
              <span>
                {activity.content.minimum_duration}
                {activity.content.maximum_duration && ` - ${activity.content.maximum_duration}`}{' '}
                {!activity.content.review_time_embedded && t('minutesPlusReviewTime')}
              </span>
            </div>
            <div className='flex items-center'>
              <Users className='mr-1 h-4 w-4' />
              <span>
                {activity.content.minimum_participants} - {activity.content.maximum_participants} {t('participants')}
              </span>
            </div>
          </div>
          <div className='mt-2 not-prose [&_*]:break-normal'>
            {showActivitySummary && <ManualRichTextRenderer document={activity.content.summary} />}
            <ManualRichTextRenderer document={activity.content.discussion_topics} />
          </div>
        </div>
        <Link
          href={activityPath}
          className='ml-auto p-2 text-blue-600 hover:text-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition-colors duration-200 flex-shrink-0 rounded-full h-10 w-10 flex items-center justify-center'
          aria-label='View activity'
        >
          <ArrowRight className='h-6 w-6' />
        </Link>
      </div>
    </div>
  )
}

export { ActivityPreviewCard }
